/**
 * sectorCatalog — Liste sectorielle centralisée pour Noam (V23.12).
 *
 * Source unique de vérité pour tous les dropdowns/comboboxes "Secteur d'activité".
 * Catégorisé pour navigation rapide dans les UI. ~95 secteurs prédéfinis +
 * acceptation de valeurs libres ("paysagisme spécialisé montagne", etc.).
 *
 * Toute valeur libre saisie est envoyée à POST /api/sectors/suggest pour
 * apprentissage : si un secteur revient souvent, on l'ajoute officiellement.
 */

export const SECTOR_CATALOG = {
  "Finance & Patrimoine": [
    "Banque privée", "Banque retail", "Banque d'affaires", "Banque centrale",
    "Assurance vie", "Assurance dommages", "Réassurance",
    "Asset management", "Hedge fund", "Private equity", "Venture capital",
    "Family office", "Wealth management",
    "Courtage en assurance", "Courtage immobilier", "Courtage financier",
    "Fintech", "Cryptomonnaies & blockchain", "Crowdfunding",
    "Fiduciaire", "Comptabilité & audit", "Conseil fiscal",
  ],
  "Immobilier & Construction": [
    "Promotion immobilière", "Investissement immobilier",
    "Gestion immobilière (gérance)", "Property management",
    "Régie immobilière", "Agence immobilière (résidentiel)", "Agence immobilière (commercial)",
    "Architecture", "Urbanisme & aménagement", "Bureau d'études (BIM, génie civil)",
    "Construction générale (entreprise totale)", "Maçonnerie", "Gros œuvre",
    "Second œuvre (plâtrerie, peinture)", "Charpente & menuiserie", "Couverture & ferblanterie",
    "Électricité bâtiment", "Plomberie & sanitaire", "Chauffage & ventilation (CVC)",
    "Façades & isolation", "Rénovation énergétique",
    "Paysagisme & jardinerie", "Travaux publics & génie civil",
    "Diagnostic & expertise bâtiment", "Notariat & droit immobilier",
  ],
  "Santé & Sciences du vivant": [
    "Hôpital & clinique", "Médecine de ville (cabinet)", "Médecine spécialisée",
    "Pharmacie & officine", "Pharmaceutique (industrie)", "Biotechnologie",
    "Dispositifs médicaux (MedTech)", "Diagnostic & imagerie",
    "Soins infirmiers & à domicile", "EMS & gériatrie",
    "Dentaire", "Optique & audition", "Psychothérapie", "Médecine vétérinaire",
    "Wellness & spa", "Médecine du sport",
  ],
  "Tech & Numérique": [
    "Édition de logiciels (SaaS)", "Développement sur mesure (agence dev)",
    "ESN / société de conseil IT", "Hébergement & cloud",
    "Cybersécurité", "Intelligence artificielle & data",
    "E-commerce", "Marketplace", "Plateforme digitale",
    "Web design & UX", "Gaming & e-sport", "Réalité virtuelle / augmentée",
    "Télécommunications", "Hardware & semi-conducteurs",
  ],
  "Industrie & Énergie": [
    "Horlogerie & joaillerie", "Industrie pharmaceutique",
    "Industrie chimique", "Industrie agroalimentaire",
    "Métallurgie & mécanique de précision", "Automobile & équipementier",
    "Aéronautique & spatial", "Naval",
    "Énergie (production & distribution)", "Énergies renouvelables",
    "Pétrole & gaz", "Mines & matières premières",
    "Recyclage & économie circulaire", "Eau & traitement",
    "Logistique industrielle", "Maintenance industrielle",
  ],
  "Services professionnels": [
    "Conseil en stratégie", "Conseil en management & organisation",
    "Conseil RH & recrutement", "Cabinet d'avocats", "Notariat",
    "Marketing & communication", "Publicité & média planning",
    "Relations publiques", "Veille stratégique & intelligence économique",
    "Études de marché & sondages", "Design industriel",
    "Traduction & interprétariat", "Formation & coaching",
    "Sécurité privée & gardiennage", "Nettoyage industriel",
    "Conciergerie d'entreprise",
  ],
  "Commerce & Distribution": [
    "Distribution alimentaire (retail)", "Grande distribution", "E-commerce généraliste",
    "Luxe & mode", "Cosmétique & beauté", "Bijouterie & joaillerie",
    "Mobilier & décoration", "Bricolage & jardin", "Sport & loisirs",
    "Automobile (concession)", "Distribution spécialisée B2B", "Import-export & négoce",
  ],
  "Hôtellerie & Restauration": [
    "Hôtellerie de luxe", "Hôtellerie d'affaires", "Hôtellerie économique",
    "Restauration gastronomique", "Restauration traditionnelle",
    "Restauration rapide", "Traiteur événementiel",
    "Bar & œnologie", "Tourisme & voyage", "Croisière & yachting",
  ],
  "Public & Institutions": [
    "Administration publique (État / canton / commune)",
    "Collectivité territoriale",
    "ONG / association à but non lucratif",
    "Fondation", "Coopérative",
    "Organisation internationale", "Diplomatie & affaires étrangères",
    "Défense & sécurité nationale", "Éducation publique",
    "Université & recherche", "École privée",
  ],
  "Arts, Culture & Loisirs": [
    "Production audiovisuelle (cinéma, TV)", "Édition (livre, presse)",
    "Musique & spectacle vivant", "Théâtre & danse",
    "Musée & patrimoine culturel", "Galerie d'art & marché de l'art",
    "Sport professionnel", "Événementiel & congrès",
    "Loisirs & parcs d'attractions", "Casino & jeux d'argent",
  ],
  "Transport & Mobilité": [
    "Transport aérien", "Transport maritime",
    "Transport ferroviaire", "Transport routier (fret)",
    "Logistique & supply chain", "Express & messagerie",
    "Mobilité urbaine (taxis, VTC, micro-mobilité)",
    "Location de véhicules", "Gestion de flotte",
  ],
  "Agriculture & Agroalimentaire": [
    "Agriculture conventionnelle", "Agriculture biologique",
    "Viticulture & œnologie", "Élevage", "Pêche & aquaculture",
    "Forestier & sylviculture", "Horticulture",
    "Transformation alimentaire", "Boulangerie-pâtisserie & artisanat alimentaire",
  ],
};

// Liste plate pour recherche / fallback
export const SECTOR_FLAT = (() => {
  const all = [];
  for (const [category, items] of Object.entries(SECTOR_CATALOG)) {
    for (const it of items) all.push({ category, label: it });
  }
  return all;
})();

// Recherche par chaîne (case-insensitive, accent-insensitive)
export function searchSectors(query, limit = 12) {
  const q = (query || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  if (!q) return SECTOR_FLAT.slice(0, limit);
  const matches = SECTOR_FLAT.filter((s) => {
    const norm = s.label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const cat = s.category.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return norm.includes(q) || cat.includes(q);
  });
  return matches.slice(0, limit);
}

// Détecte si une valeur arbitraire correspond exactement à un secteur du catalogue.
export function isCatalogValue(value) {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return SECTOR_FLAT.some((s) => s.label.toLowerCase() === v);
}
